(function () {
  const existingApi = window.MeasurementPricing;

  if (existingApi && existingApi.loaded) {
    existingApi.initMeasurementProductForms();
    existingApi.initMeasurementCartRemoval();
    return;
  }

  function route(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path.replace(/^\//, '')}`;
  }

  function normalizeDecimalText(value) {
    return String(value ?? '')
      .trim()
      .replace(/[^\d,.\-]/g, '');
  }

  function parseDecimal(value) {
    if (typeof value === 'number') return value;

    let text = normalizeDecimalText(value);
    if (!text) return NaN;

    if (text.includes(',')) {
      text = text.replace(/\./g, '').replace(',', '.');
    }

    return Number(text);
  }

  function parseMeasureToCm(value) {
    if (typeof value === 'number') {
      if (!Number.isFinite(value) || value <= 0) throw new Error('Medida invalida.');
      return Math.round(value);
    }

    const rawValue = String(value ?? '').trim().toLowerCase();
    const normalizedValue = rawValue
      .replace(/centimetros?/g, 'cm')
      .replace(/centímetros?/g, 'cm')
      .replace(/metros?/g, 'm');
    const hasCentimeterUnit = /\d\s*cm\b/.test(normalizedValue);
    const hasMeterUnit = /\d\s*m\b/.test(normalizedValue) && !hasCentimeterUnit;
    const cleanValue = normalizedValue
      .replace(/cm\b/g, '')
      .replace(/m\b/g, '')
      .trim();
    const number = parseDecimal(cleanValue);

    if (!Number.isFinite(number) || number <= 0) {
      throw new Error('Medida invalida.');
    }

    if (!hasCentimeterUnit && (hasMeterUnit || (/[,.]/.test(cleanValue) && number <= 10))) {
      return Math.round(number * 100);
    }

    return Math.round(number);
  }

  function roundUpToInterval(value, interval) {
    const numericValue = Number(value);
    const numericInterval = Number(interval);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      throw new Error('Medida invalida.');
    }

    if (!Number.isFinite(numericInterval) || numericInterval <= 0) {
      throw new Error('Intervalo de arredondamento invalido.');
    }

    return Math.ceil(numericValue / numericInterval) * numericInterval;
  }

  // O arredondamento do intervalo e aplicado em apenas um dos lados: o que gerar
  // o menor acrescimo de area (ou seja, o menor desperdicio pago pelo cliente).
  function chooseRoundedSide(lengthCm, widthCm, roundIntervalCm) {
    const lengthRoundedCm = roundUpToInterval(lengthCm, roundIntervalCm);
    const widthRoundedCm = roundUpToInterval(widthCm, roundIntervalCm);
    const wasteByLength = (lengthRoundedCm - lengthCm) * widthCm;
    const wasteByWidth = (widthRoundedCm - widthCm) * lengthCm;

    if (wasteByWidth < wasteByLength) {
      return {
        lengthChargedCm: lengthCm,
        widthChargedCm: widthRoundedCm,
        roundedSide: 'width',
      };
    }

    return {
      lengthChargedCm: lengthRoundedCm,
      widthChargedCm: widthCm,
      roundedSide: 'length',
    };
  }

  function formatMoneyFromCents(cents) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Number(cents) / 100);
  }

  function formatInteger(value) {
    return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  }

  function formatMeters(centimeters) {
    return `${(Number(centimeters) / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}m`;
  }

  function createGroupId() {
    return `measure-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function normalizeSettingPricePer100Cm2(value) {
    const price = parseDecimal(value);
    if (!Number.isFinite(price) || price <= 0) return NaN;

    return price < 1 ? price * 100 : price;
  }

  function positiveOrNaN(value) {
    const price = parseDecimal(value);
    return Number.isFinite(price) && price > 0 ? price : NaN;
  }

  // Fonte do preco em ordem de prioridade. Tudo e convertido para R$ por cm2,
  // que e a unidade canonica usada no calculo.
  function getConfiguredPricePerCm2(form) {
    const data = form.dataset;

    const metafieldPerCm2 = positiveOrNaN(data.pricePerCm2Metafield);
    if (Number.isFinite(metafieldPerCm2)) return metafieldPerCm2;

    const metafieldPerM2 = positiveOrNaN(data.pricePerM2Metafield);
    if (Number.isFinite(metafieldPerM2)) return metafieldPerM2 / 10000;

    const metafieldPer100Cm2 = positiveOrNaN(data.priceModule100Cm2);
    if (Number.isFinite(metafieldPer100Cm2)) return metafieldPer100Cm2 / 100;

    const settingPerM2 = positiveOrNaN(data.pricePerM2Setting);
    if (Number.isFinite(settingPerM2)) return settingPerM2 / 10000;

    const settingPer100Cm2 = normalizeSettingPricePer100Cm2(data.priceSetting || data.pricePerCm2);
    if (Number.isFinite(settingPer100Cm2)) return settingPer100Cm2 / 100;

    throw new Error('Configure o preco por cm2 (ou por m2) do produto sob medida.');
  }

  // Mantido para compatibilidade com integracoes que ja usavam a API antiga.
  function getConfiguredPricePer100Cm2(form) {
    return getConfiguredPricePerCm2(form) * 100;
  }

  function normalizeBorder(border) {
    if (!border) return null;

    const pricePerMeter = parseDecimal(border.pricePerMeter);
    const name = String(border.name || '').trim();

    if (!name) return null;

    return {
      name,
      pricePerMeter: Number.isFinite(pricePerMeter) && pricePerMeter > 0 ? pricePerMeter : 0,
    };
  }

  // O acabamento e cobrado por metro linear somando os 4 lados informados pelo
  // cliente (perimetro), sem o arredondamento aplicado a area.
  function calculateBorderPricing({ lengthCm, widthCm, border }) {
    const normalizedBorder = normalizeBorder(border);
    const perimeterCm = 2 * (Number(lengthCm) + Number(widthCm));

    if (!normalizedBorder || normalizedBorder.pricePerMeter <= 0) {
      return {
        border: normalizedBorder,
        perimeterCm,
        perimeterMeters: perimeterCm / 100,
        borderPriceCents: 0,
      };
    }

    const perimeterMeters = perimeterCm / 100;

    return {
      border: normalizedBorder,
      perimeterCm,
      perimeterMeters,
      borderPriceCents: Math.round(perimeterMeters * normalizedBorder.pricePerMeter * 100),
    };
  }

  function calculateMeasurementPricing({
    lengthCm,
    widthCm,
    pricePerCm2,
    pricePer100Cm2,
    roundIntervalCm,
    mainUnitPriceCents,
    border,
  }) {
    let commercialPricePerCm2 = Number(pricePerCm2);

    if (!Number.isFinite(commercialPricePerCm2) || commercialPricePerCm2 <= 0) {
      commercialPricePerCm2 = Number(pricePer100Cm2) / 100;
    }

    const { lengthChargedCm, widthChargedCm, roundedSide } = chooseRoundedSide(
      lengthCm,
      widthCm,
      roundIntervalCm
    );
    const stockArea = lengthCm * widthCm;
    const chargedArea = lengthChargedCm * widthChargedCm;
    const matPriceCents = Math.round(chargedArea * commercialPricePerCm2 * 100);
    const borderCalculation = calculateBorderPricing({ lengthCm, widthCm, border });
    const finalPriceCents = matPriceCents + borderCalculation.borderPriceCents;
    const mainLineCents = stockArea * mainUnitPriceCents;
    const adjustmentCents = matPriceCents - mainLineCents;

    return {
      lengthChargedCm,
      widthChargedCm,
      roundedSide,
      stockArea,
      chargedArea,
      pricePerCm2: commercialPricePerCm2,
      matPriceCents,
      border: borderCalculation.border,
      perimeterCm: borderCalculation.perimeterCm,
      perimeterMeters: borderCalculation.perimeterMeters,
      borderPriceCents: borderCalculation.borderPriceCents,
      finalPriceCents,
      mainLineCents,
      adjustmentCents,
    };
  }

  // Quebra um valor em centavos nas duas variantes auxiliares (R$ 1,00 e R$ 0,01).
  function buildAuxiliaryLines({
    cents,
    adjustmentRealVariantId,
    adjustmentCentVariantId,
    baseProperties,
  }) {
    const realQty = Math.floor(cents / 100);
    const centQty = cents % 100;
    const lines = [];

    if (realQty > 0) {
      if (!adjustmentRealVariantId) {
        throw new Error('Configure o ID da variante auxiliar de R$ 1,00.');
      }

      lines.push({
        id: Number(adjustmentRealVariantId),
        quantity: realQty,
        properties: { ...baseProperties },
      });
    }

    if (centQty > 0) {
      if (!adjustmentCentVariantId) {
        throw new Error('Configure o ID da variante auxiliar de R$ 0,01.');
      }

      lines.push({
        id: Number(adjustmentCentVariantId),
        quantity: centQty,
        properties: { ...baseProperties },
      });
    }

    return { lines, realQty, centQty };
  }

  function buildMeasurementCartItems({
    productVariantId,
    mainUnitPriceCents,
    lengthCm,
    widthCm,
    pricePerCm2,
    pricePer100Cm2,
    roundIntervalCm,
    border,
    adjustmentRealVariantId,
    adjustmentCentVariantId,
  }) {
    const calculation = calculateMeasurementPricing({
      lengthCm,
      widthCm,
      pricePerCm2,
      pricePer100Cm2,
      roundIntervalCm,
      mainUnitPriceCents,
      border,
    });

    if (calculation.adjustmentCents < 0) {
      throw new Error(
        'O preco unitario do produto principal esta maior que o preco final calculado. Reduza o preco base do produto principal.'
      );
    }

    const groupId = createGroupId();
    const mainProperties = {
      Comprimento: `${lengthCm}cm`,
      'Largura escolhida': `${widthCm}cm`,
      'Largura cobrada': `${calculation.widthChargedCm}cm`,
      'Comprimento cobrado': `${calculation.lengthChargedCm}cm`,
      'Area real para estoque': `${calculation.stockArea}cm2`,
      'Area cobrada': `${calculation.chargedArea}cm2`,
      'Valor do tapete': formatMoneyFromCents(calculation.matPriceCents),
      _measurement_product: 'true',
      _calc_group_id: groupId,
      // O preco exibido na linha do tapete ja inclui o acabamento: as linhas
      // auxiliares da borda ficam ocultas para o carrinho mostrar um item so.
      _display_final_cents: String(calculation.finalPriceCents),
      _stock_area: String(calculation.stockArea),
      _charged_area: String(calculation.chargedArea),
    };

    if (calculation.border) {
      mainProperties.Acabamento = calculation.border.name;

      if (calculation.borderPriceCents > 0) {
        const borderUnitPrice = formatMoneyFromCents(
          Math.round(calculation.border.pricePerMeter * 100)
        );

        mainProperties['Valor do acabamento'] = `${formatMeters(
          calculation.perimeterCm
        )} lineares x ${borderUnitPrice} = ${formatMoneyFromCents(calculation.borderPriceCents)}`;
      }
    }

    const items = [
      {
        id: Number(productVariantId),
        quantity: calculation.stockArea,
        properties: mainProperties,
      },
    ];

    const matAdjustment = buildAuxiliaryLines({
      cents: calculation.adjustmentCents,
      adjustmentRealVariantId,
      adjustmentCentVariantId,
      baseProperties: {
        _is_measurement_adjustment: 'true',
        _calc_group_id: groupId,
      },
    });

    items.push(...matAdjustment.lines);

    let borderAdjustment = { lines: [], realQty: 0, centQty: 0 };

    if (calculation.borderPriceCents > 0 && calculation.border) {
      borderAdjustment = buildAuxiliaryLines({
        cents: calculation.borderPriceCents,
        adjustmentRealVariantId,
        adjustmentCentVariantId,
        baseProperties: {
          _is_measurement_adjustment: 'true',
          _is_border_line: 'true',
          _calc_group_id: groupId,
        },
      });

      items.push(...borderAdjustment.lines);
    }

    return {
      items,
      calculation,
      groupId,
      adjustmentRealQty: matAdjustment.realQty,
      adjustmentCentQty: matAdjustment.centQty,
      borderRealQty: borderAdjustment.realQty,
      borderCentQty: borderAdjustment.centQty,
    };
  }

  async function addMeasurementProductToCart(config) {
    const builtItems = buildMeasurementCartItems(config);
    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: builtItems.items }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.description || error?.message || 'Erro ao adicionar produto por medida ao carrinho.');
    }

    return {
      response: await response.json(),
      ...builtItems,
    };
  }

  async function removeMeasurementGroup(groupId) {
    if (!groupId) {
      throw new Error('Grupo de medida invalido.');
    }

    const cartResponse = await fetch(route('cart.js'), {
      headers: { Accept: 'application/json' },
    });

    if (!cartResponse.ok) {
      throw new Error('Erro ao buscar o carrinho.');
    }

    const cart = await cartResponse.json();
    const updates = {};

    cart.items.forEach((item) => {
      if (item.properties && item.properties._calc_group_id === groupId) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) {
      throw new Error('Nao foi possivel localizar o produto por medida no carrinho.');
    }

    const response = await fetch(route('cart/update.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error('Erro ao remover produto por medida.');
    }

    return response.json();
  }

  function parseVariants(form) {
    const script = form.querySelector('script[data-measurement-variants]');
    if (!script?.textContent) return {};

    try {
      const variants = JSON.parse(script.textContent);
      return variants.reduce((accumulator, variant) => {
        accumulator[String(variant.id)] = variant;
        return accumulator;
      }, {});
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  function getSelectedVariantId(form, variants) {
    const productId = form.dataset.productId;
    const section = form.closest('.shopify-section') || document;
    const picker = section.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const selectedOption = picker?.querySelector('select option:checked[data-variant-id], fieldset input:checked[data-variant-id]');
    const selectedOptionVariantId = selectedOption?.dataset.variantId;

    if (selectedOptionVariantId && variants[selectedOptionVariantId]) {
      return selectedOptionVariantId;
    }

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    if (urlVariantId && variants[urlVariantId]) {
      return urlVariantId;
    }

    return form.dataset.selectedVariantId;
  }

  function getCurrentVariant(form, variants) {
    const selectedVariantId = getSelectedVariantId(form, variants);
    const variant = variants[String(selectedVariantId)];

    if (variant) return variant;

    return {
      id: form.dataset.selectedVariantId,
      price: Number(form.dataset.selectedVariantPriceCents || 0),
      available: true,
    };
  }

  function hasBorderOptions(form) {
    return form.querySelectorAll('[data-measurement-border]').length > 0;
  }

  // Aceita tanto o <select> de acabamento quanto o formato antigo de radios.
  function getSelectedBorder(form) {
    const control = form.querySelector('[data-measurement-border]');

    if (!control) return null;

    if (control.tagName === 'SELECT') {
      const option = control.selectedOptions[0];

      if (!option?.dataset.borderName) return null;

      return normalizeBorder({
        name: option.dataset.borderName,
        pricePerMeter: option.dataset.borderPrice,
      });
    }

    const checkedInput = form.querySelector('[data-measurement-border]:checked');

    if (!checkedInput) return null;

    return normalizeBorder({
      name: checkedInput.dataset.borderName || checkedInput.value,
      pricePerMeter: checkedInput.dataset.borderPrice,
    });
  }

  function requireBorderSelection(form) {
    const border = getSelectedBorder(form);

    if (!border && hasBorderOptions(form) && form.dataset.borderRequired === 'true') {
      throw new Error('Escolha um acabamento (borda) para continuar.');
    }

    return border;
  }

  function setError(form, message) {
    const errorElement = form.querySelector('[data-measurement-error]');

    if (!errorElement) return;

    errorElement.textContent = message || '';
    errorElement.hidden = !message;
  }

  function setButtonLoading(button, isLoading) {
    const label = button.querySelector('[data-measurement-button-label]') || button;

    if (!button.dataset.defaultLabel) {
      button.dataset.defaultLabel = label.textContent || 'Adicionar ao Carrinho';
    }

    button.disabled = isLoading;
    label.textContent = isLoading ? 'Processando...' : button.dataset.defaultLabel;
  }

  function updatePreview(form, variants) {
    const widthInput = form.querySelector('[data-measurement-width]');
    const lengthInput = form.querySelector('[data-measurement-length]');
    const stockAreaElement = form.querySelector('[data-measurement-stock-area]');
    const chargedWidthElement = form.querySelector('[data-measurement-charged-width]');
    const chargedLengthElement = form.querySelector('[data-measurement-charged-length]');
    const chargedAreaElement = form.querySelector('[data-measurement-charged-area]');
    const matPriceElement = form.querySelector('[data-measurement-mat-price]');
    const borderRow = form.querySelector('[data-measurement-border-row]');
    const borderNameElement = form.querySelector('[data-measurement-border-name]');
    const borderSidesElement = form.querySelector('[data-measurement-border-sides]');
    const borderPerimeterElement = form.querySelector('[data-measurement-border-perimeter]');
    const borderUnitElement = form.querySelector('[data-measurement-border-unit]');
    const borderPriceElement = form.querySelector('[data-measurement-border-price]');
    const totalElement = form.querySelector('[data-measurement-total]');
    const button = form.querySelector('[data-measurement-submit]');

    setError(form, '');

    if (borderRow) borderRow.hidden = true;

    if (!widthInput?.value || !lengthInput?.value) {
      if (stockAreaElement) stockAreaElement.textContent = '0cm2';
      if (chargedWidthElement) chargedWidthElement.textContent = '0cm';
      if (chargedLengthElement) chargedLengthElement.textContent = '0cm';
      if (chargedAreaElement) chargedAreaElement.textContent = '0cm2';
      if (matPriceElement) matPriceElement.textContent = 'R$ 0,00';
      if (totalElement) totalElement.textContent = 'R$ 0,00';
      if (button) button.disabled = false;
      return;
    }

    try {
      const widthCm = parseMeasureToCm(widthInput.value);
      const lengthCm = parseMeasureToCm(lengthInput.value);
      const variant = getCurrentVariant(form, variants);
      const calculation = calculateMeasurementPricing({
        lengthCm,
        widthCm,
        pricePerCm2: getConfiguredPricePerCm2(form),
        roundIntervalCm: Number(form.dataset.roundIntervalCm || 20),
        mainUnitPriceCents: Number(variant.price || 0),
        border: getSelectedBorder(form),
      });

      if (stockAreaElement) stockAreaElement.textContent = `${formatInteger(calculation.stockArea)}cm2`;
      if (chargedWidthElement) chargedWidthElement.textContent = `${formatInteger(calculation.widthChargedCm)}cm`;
      if (chargedLengthElement) chargedLengthElement.textContent = `${formatInteger(calculation.lengthChargedCm)}cm`;
      if (chargedAreaElement) chargedAreaElement.textContent = `${formatInteger(calculation.chargedArea)}cm2`;
      if (matPriceElement) matPriceElement.textContent = formatMoneyFromCents(calculation.matPriceCents);
      if (totalElement) totalElement.textContent = formatMoneyFromCents(calculation.finalPriceCents);

      // O resumo do acabamento aparece assim que uma borda e escolhida, inclusive
      // quando ela nao tem custo, para o cliente conferir o que selecionou.
      if (borderRow && calculation.border) {
        borderRow.hidden = false;

        if (borderNameElement) borderNameElement.textContent = calculation.border.name;

        if (borderSidesElement) {
          borderSidesElement.textContent = [widthCm, widthCm, lengthCm, lengthCm]
            .map(formatMeters)
            .join(' + ');
        }

        if (borderPerimeterElement) {
          borderPerimeterElement.textContent = formatMeters(calculation.perimeterCm);
        }

        if (borderUnitElement) {
          borderUnitElement.textContent =
            calculation.border.pricePerMeter > 0
              ? formatMoneyFromCents(Math.round(calculation.border.pricePerMeter * 100))
              : 'sem custo adicional';
        }

        if (borderPriceElement) {
          borderPriceElement.textContent = formatMoneyFromCents(calculation.borderPriceCents);
        }
      }

      if (button) button.disabled = calculation.adjustmentCents < 0;
      if (calculation.adjustmentCents < 0) {
        setError(form, 'O preco base do produto principal esta maior que o preco calculado.');
      }
    } catch (error) {
      if (button) button.disabled = false;
      if (matPriceElement) matPriceElement.textContent = 'R$ 0,00';
      if (totalElement) totalElement.textContent = 'R$ 0,00';
    }
  }

  function initMeasurementProductForms() {
    document.querySelectorAll('[data-measurement-form]').forEach((form) => {
      if (form.dataset.measurementInitialized === 'true') return;

      form.dataset.measurementInitialized = 'true';
      const variants = parseVariants(form);
      const widthInput = form.querySelector('[data-measurement-width]');
      const lengthInput = form.querySelector('[data-measurement-length]');
      const submitButton = form.querySelector('[data-measurement-submit]');
      const section = form.closest('.shopify-section') || document;

      const refreshPreview = () => updatePreview(form, variants);

      widthInput?.addEventListener('input', refreshPreview);
      lengthInput?.addEventListener('input', refreshPreview);

      form.querySelectorAll('[data-measurement-border]').forEach((input) => {
        input.addEventListener('change', refreshPreview);
      });

      section.addEventListener('variant:update', (event) => {
        const eventProductId = event.detail?.data?.productId;
        const variant = event.detail?.resource;

        if (eventProductId && eventProductId !== form.dataset.productId) return;
        if (!variant?.id) return;

        form.dataset.selectedVariantId = String(variant.id);
        form.dataset.selectedVariantPriceCents = String(variant.price || 0);
        variants[String(variant.id)] = variant;
        refreshPreview();
      });

      section.addEventListener('variant:selected', () => {
        window.setTimeout(refreshPreview, 0);
      });

      submitButton?.addEventListener('click', async (event) => {
        event.preventDefault();
        setError(form, '');

        try {
          const variant = getCurrentVariant(form, variants);

          if (!variant?.id) {
            throw new Error('Produto indisponivel.');
          }

          if (variant.available === false) {
            throw new Error('Variante indisponivel.');
          }

          const widthCm = parseMeasureToCm(widthInput?.value);
          const lengthCm = parseMeasureToCm(lengthInput?.value);
          const border = requireBorderSelection(form);

          setButtonLoading(submitButton, true);

          await addMeasurementProductToCart({
            productVariantId: variant.id,
            mainUnitPriceCents: Number(variant.price || form.dataset.selectedVariantPriceCents || 0),
            lengthCm,
            widthCm,
            pricePerCm2: getConfiguredPricePerCm2(form),
            roundIntervalCm: Number(form.dataset.roundIntervalCm || 20),
            border,
            adjustmentRealVariantId: form.dataset.adjustmentRealVariantId,
            adjustmentCentVariantId: form.dataset.adjustmentCentVariantId,
          });

          window.location.href = route('cart');
        } catch (error) {
          console.error(error);
          setError(form, error.message || 'Erro ao adicionar produto por medida ao carrinho.');
          setButtonLoading(submitButton, false);
        }
      });

      refreshPreview();
    });
  }

  function initMeasurementCartRemoval() {
    if (window.MeasurementPricing.cartRemovalBound) return;

    window.MeasurementPricing.cartRemovalBound = true;

    document.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('[data-measure-remove-group]');
      if (!button) return;

      event.preventDefault();

      try {
        button.setAttribute('disabled', 'disabled');
        await removeMeasurementGroup(button.dataset.measureRemoveGroup);
        window.location.reload();
      } catch (error) {
        console.error(error);
        button.removeAttribute('disabled');
        window.alert(error.message || 'Erro ao remover produto por medida.');
      }
    });
  }

  window.MeasurementPricing = {
    loaded: true,
    cartRemovalBound: false,
    parseMeasureToCm,
    roundUpToInterval,
    chooseRoundedSide,
    calculateBorderPricing,
    calculateMeasurementPricing,
    formatMoneyFromCents,
    formatMeters,
    createGroupId,
    getConfiguredPricePerCm2,
    getConfiguredPricePer100Cm2,
    getSelectedBorder,
    buildMeasurementCartItems,
    addMeasurementProductToCart,
    removeMeasurementGroup,
    initMeasurementProductForms,
    initMeasurementCartRemoval,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initMeasurementProductForms();
      initMeasurementCartRemoval();
    });
  } else {
    initMeasurementProductForms();
    initMeasurementCartRemoval();
  }
})();
